import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BranchStatsDTO, PriceStatsDTO, ProductStatsDTO, ProfitStatsDTO, SalesStatsDTO, StatsPeriodDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, EmptyState, Kpi, PageHeader, Select, Skeleton } from '@bifeai/ui';
import { ChartColumn, Download, PackageMinus, Receipt, ShoppingCart, TrendingUp, Wallet, Weight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { downloadCsv, kilos, pct as pctNum, pesos } from '../lib/csv';
import { formatDate, formatDateTime, formatKilos, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { formatQty, todayIso } from '../lib/units';
import { Delta, PeriodPicker, TrendChart, type Period } from './DashboardPage';

type Tab = 'sales' | 'products' | 'profit' | 'prices' | 'branches';
const PERIOD_KEY = 'bifeai.stats.period';
const pct = (r: number | null) => (r === null ? '—' : `${(r * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })} %`);
const signed = (r: number | null) => (r === null ? '—' : `${r > 0 ? '+' : ''}${(r * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })} %`);

function loadPeriod(): Period {
  try {
    const p = JSON.parse(localStorage.getItem(PERIOD_KEY) ?? 'null') as Period | null;
    if (p?.range) return p;
  } catch {
    /* sin almacenamiento */
  }
  return { range: '30d', from: todayIso(), to: todayIso() };
}

function usePeriodQuery<T>(key: string, path: string, period: Period, enabled = true) {
  const { selectedBranchId } = useSession();
  const qs = new URLSearchParams({ range: period.range });
  if (period.range === 'custom') {
    qs.set('from', period.from);
    qs.set('to', period.to);
  }
  return useQuery({
    queryKey: ['stats', key, selectedBranchId, qs.toString()],
    queryFn: () => api.get<T>(`${path}?${qs.toString()}`),
    placeholderData: keepPreviousData,
    enabled,
  });
}

const periodText = (p: StatsPeriodDTO) => (p.from === p.to ? formatDate(p.from) : `${formatDate(p.from)} al ${formatDate(p.to)}`);
const fileName = (name: string, p: StatsPeriodDTO) => `bifeai-${name}-${p.from}-a-${p.to}`;

function ExcelButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" icon={<Download />} onClick={onClick}>
      Descargar Excel
    </Button>
  );
}

/** Estadísticas: el análisis a fondo (el dashboard muestra el resumen). */
export function StatsPage() {
  const { can, multiBranch, isHeadquartersUser } = useSession();
  const canCost = can('costs.view');
  const [period, setPeriod] = useState<Period>(loadPeriod);
  const [tab, setTab] = useState<Tab>('sales');
  useEffect(() => {
    try {
      localStorage.setItem(PERIOD_KEY, JSON.stringify(period));
    } catch {
      /* ignorar */
    }
  }, [period]);
  const tabs: [Tab, string][] = [
    ['sales', 'Ventas'],
    ['products', 'Productos'],
    ...(canCost
      ? ([
          ['profit', 'Ganancia'],
          ['prices', 'Costo vs. precio'],
        ] as [Tab, string][])
      : []),
    ...(multiBranch && isHeadquartersUser ? ([['branches', 'Sucursales']] as [Tab, string][]) : []),
  ];

  return (
    <>
      <PageHeader title="Estadísticas" subtitle="Cómo vendés, qué te deja plata, qué no se mueve y cómo te afectan los aumentos." />
      <PeriodPicker value={period} onChange={setPeriod} />
      <div className="bf-tabs" role="tablist">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'sales' ? (
        <SalesTab period={period} />
      ) : tab === 'products' ? (
        <ProductsTab period={period} />
      ) : tab === 'profit' ? (
        <ProfitTab period={period} />
      ) : tab === 'prices' ? (
        <PricesTab period={period} />
      ) : (
        <BranchesTab period={period} />
      )}
    </>
  );
}

// ---------------------------------------------------------------- ventas
const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const ORDER = [1, 2, 3, 4, 5, 6, 0];

function SalesTab({ period }: { period: Period }) {
  const q = usePeriodQuery<SalesStatsDTO>('sales', '/stats/sales', period);
  const d = q.data;
  if (!d) return <Skeleton style={{ height: 300 }} />;
  const t = d.totals;
  const hours = d.heatmap.map((h) => h.hour);
  const minH = hours.length ? Math.min(...hours) : 8;
  const maxH = hours.length ? Math.max(...hours) : 20;
  const maxCell = Math.max(...d.heatmap.map((h) => h.salesCents), 1);
  const cellOf = (dow: number, hour: number) => d.heatmap.find((h) => h.dow === dow && h.hour === hour);
  const best = [...d.heatmap].sort((a, b) => b.salesCents - a.salesCents)[0];

  const excel = () =>
    downloadCsv(
      fileName('ventas', d.period),
      ['Fecha', 'Ventas ($)', 'Tickets', 'Período anterior ($)'],
      d.series.map((s) => [s.date, pesos(s.salesCents), s.salesCount, pesos(s.previousCents)]),
    );

  return (
    <>
      <div className="bf-kpi-grid">
        <Kpi
          label="Ventas"
          tone="celeste"
          icon={<TrendingUp />}
          value={formatMoney(t.salesCents)}
          delta={<Delta now={t.salesCents} before={d.previous.salesCents} money />}
        />
        <Kpi
          label="Tickets"
          tone="green"
          icon={<Receipt />}
          value={t.count.toLocaleString('es-AR')}
          delta={<Delta now={t.count} before={d.previous.count} />}
        />
        <Kpi
          label="Ticket promedio"
          tone="amber"
          icon={<ShoppingCart />}
          value={formatMoney(t.avgTicketCents)}
          delta={<Delta now={t.avgTicketCents} before={d.previous.avgTicketCents} money />}
        />
        <Kpi
          label="Kilos vendidos"
          tone="red"
          icon={<Weight />}
          value={formatKilos(t.grams)}
          delta={<Delta now={t.grams} before={d.previous.grams} />}
        />
      </div>
      <Card>
        <CardHeader
          title={`Ventas del ${periodText(d.period)}`}
          subtitle={
            d.lastYear.salesCents > 0
              ? `Mismo período del año pasado: ${formatMoney(d.lastYear.salesCents)} (${signed((t.salesCents - d.lastYear.salesCents) / d.lastYear.salesCents)}).`
              : 'Todavía no hay datos del mismo período del año pasado.'
          }
          actions={<ExcelButton onClick={excel} />}
        />
        <CardBody>
          <TrendChart points={d.series} granularity={d.period.granularity} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="Días y horarios con más venta"
          subtitle={
            best
              ? `El momento más fuerte: ${DAYS[best.dow]} de ${best.hour} a ${best.hour + 1} h. Sirve para saber cuándo hace falta más gente en el mostrador.`
              : 'Todavía no hay ventas en este período.'
          }
        />
        <CardBody>
          {d.heatmap.length === 0 ? (
            <EmptyState icon={<ChartColumn />} title="Sin ventas en el período" />
          ) : (
            <div className="bf-heat" style={{ gridTemplateColumns: `48px repeat(${maxH - minH + 1}, minmax(26px, 1fr))` }}>
              <span />
              {Array.from({ length: maxH - minH + 1 }, (_, i) => (
                <span key={i} className="bf-heat-h">
                  {minH + i}
                </span>
              ))}
              {ORDER.map((dow) => (
                <div key={dow} style={{ display: 'contents' }}>
                  <span className="bf-heat-d">{DAYS[dow]}</span>
                  {Array.from({ length: maxH - minH + 1 }, (_, i) => {
                    const c = cellOf(dow, minH + i);
                    const v = c ? c.salesCents / maxCell : 0;
                    return (
                      <span
                        key={i}
                        className="bf-heat-c"
                        title={
                          c
                            ? `${DAYS[dow]} ${minH + i} h: ${formatMoney(c.salesCents)} · ${c.count} tickets`
                            : `${DAYS[dow]} ${minH + i} h: sin ventas`
                        }
                        style={{ ['--v' as string]: String(Math.round(v * 100)) }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- productos
function ProductsTab({ period }: { period: Period }) {
  const { can } = useSession();
  const [sort, setSort] = useState<'money' | 'qty' | 'profit'>('money');
  const [slowDays, setSlowDays] = useState(7);
  const { selectedBranchId } = useSession();
  const qs = new URLSearchParams({ range: period.range, slowDays: String(slowDays) });
  if (period.range === 'custom') {
    qs.set('from', period.from);
    qs.set('to', period.to);
  }
  const q = useQuery({
    queryKey: ['stats', 'products', selectedBranchId, qs.toString()],
    queryFn: () => api.get<ProductStatsDTO>(`/stats/products?${qs.toString()}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  if (!d) return <Skeleton style={{ height: 300 }} />;
  const rows = [...d.rows].sort((a, b) =>
    sort === 'money' ? b.salesCents - a.salesCents : sort === 'qty' ? b.qty - a.qty : (b.profitCents ?? 0) - (a.profitCents ?? 0),
  );
  const aCount = d.rows.filter((r) => r.abc === 'A').length;
  const canCost = can('costs.view');

  const excel = () =>
    downloadCsv(
      fileName('productos', d.period),
      [
        'Producto',
        'Categoría',
        'Cantidad',
        'Unidad',
        'Ventas ($)',
        '% de la venta',
        'Grupo',
        ...(canCost ? ['Costo ($)', 'Ganancia ($)', 'Margen %'] : []),
      ],
      rows.map((r) => [
        r.name,
        r.categoryName,
        r.unitType === 'WEIGHT' ? kilos(r.qty) : r.qty,
        r.unitType === 'WEIGHT' ? 'kg' : 'u.',
        pesos(r.salesCents),
        pctNum(r.share),
        r.abc,
        ...(canCost ? [pesos(r.costCents), pesos(r.profitCents), pctNum(r.marginRatio)] : []),
      ]),
    );

  return (
    <>
      {d.rows.length > 0 && (
        <Callout tone="info">
          <strong>
            {aCount} {aCount === 1 ? 'producto hace' : 'productos hacen'} el 80 % de tu venta
          </strong>{' '}
          (grupo A, de {d.rows.length} que vendiste). Son los que nunca te pueden faltar y los que más conviene cuidar en precio.
        </Callout>
      )}
      <Card>
        <CardHeader
          title="Ranking de productos"
          actions={
            <>
              <Select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} aria-label="Ordenar" style={{ maxWidth: 200 }}>
                <option value="money">Por plata</option>
                <option value="qty">Por cantidad</option>
                {canCost && <option value="profit">Por ganancia</option>}
              </Select>
              <ExcelButton onClick={excel} />
            </>
          }
        />
        <CardBody flush>
          {rows.length === 0 ? (
            <EmptyState icon={<ChartColumn />} title="Sin ventas en el período" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Cantidad</th>
                    <th style={{ textAlign: 'right' }}>Ventas</th>
                    <th style={{ textAlign: 'right' }}>% de la venta</th>
                    {canCost && <th style={{ textAlign: 'right' }}>Ganancia</th>}
                    {canCost && <th style={{ textAlign: 'right' }}>Margen</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.productId}>
                      <td>
                        <span className="bf-cell-main">{r.name}</span>{' '}
                        <Badge tone={r.abc === 'A' ? 'success' : r.abc === 'B' ? 'info' : 'neutral'}>{r.abc}</Badge>
                        {r.categoryName && <div className="bf-cell-sub">{r.categoryName}</div>}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatQty(r.unitType, r.qty)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatMoney(r.salesCents)}
                      </td>
                      <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                        {pct(r.share)}
                      </td>
                      {canCost && (
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {formatMoney(r.profitCents ?? 0)}
                        </td>
                      )}
                      {canCost && (
                        <td className={`bf-num ${(r.marginRatio ?? 1) < 0.15 ? 'bf-text-danger' : 'bf-cell-sub'}`} style={{ textAlign: 'right' }}>
                          {pct(r.marginRatio)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="Productos que no se mueven"
          subtitle="Tienen stock y no se vendieron en los últimos días. Plata parada en la heladera: conviene promocionarlos o pasarlos a elaborados antes de que venzan."
          actions={
            <Select value={slowDays} onChange={(e) => setSlowDays(Number(e.target.value))} aria-label="Días sin venta" style={{ maxWidth: 200 }}>
              {[3, 7, 15, 30].map((n) => (
                <option key={n} value={n}>
                  Más de {n} días sin venta
                </option>
              ))}
            </Select>
          }
        />
        <CardBody flush>
          {d.slow.length === 0 ? (
            <EmptyState icon={<PackageMinus />} title="Todo lo que tenés en stock se está vendiendo" />
          ) : (
            <table className="bf-table">
              <tbody>
                {d.slow.map((s) => (
                  <tr key={s.productId}>
                    <td className="bf-cell-main">{s.name}</td>
                    <td className="bf-cell-sub">
                      {s.lastSaleAt ? `Última venta: ${formatDateTime(s.lastSaleAt)} (hace ${s.daysWithoutSale} días)` : 'Nunca se vendió'}
                    </td>
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {formatQty(s.unitType, s.onHandQty)}
                      {s.stockCostCents !== null && <div className="bf-cell-sub">{formatMoney(s.stockCostCents)} parados</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- ganancia
function ProfitTab({ period }: { period: Period }) {
  const q = usePeriodQuery<ProfitStatsDTO>('profit', '/stats/profit', period);
  const d = q.data;
  if (!d) return <Skeleton style={{ height: 300 }} />;
  const excel = () =>
    downloadCsv(
      fileName('ganancia', d.period),
      ['Categoría', 'Ventas ($)', 'Costo ($)', 'Ganancia ($)', 'Margen %'],
      d.byCategory.map((c) => [c.categoryName, pesos(c.salesCents), pesos(c.costCents), pesos(c.profitCents), pctNum(c.marginRatio)]),
    );
  return (
    <>
      <div className="bf-kpi-grid">
        <Kpi label="Ventas" tone="celeste" icon={<TrendingUp />} value={formatMoney(d.salesCents)} />
        <Kpi label="Costo de la mercadería" tone="amber" icon={<ShoppingCart />} value={formatMoney(d.costCents)} />
        <Kpi label="Ganancia bruta" tone="green" icon={<Wallet />} value={formatMoney(d.grossProfitCents)} delta={`Margen ${pct(d.marginRatio)}`} />
        <Kpi
          label="Después de la merma"
          tone="red"
          icon={<PackageMinus />}
          value={formatMoney(d.afterWasteCents)}
          delta={`Merma ${formatMoney(d.wasteCostCents)} · sebero ${formatMoney(d.tachoIncomeCents)}`}
        />
      </div>
      <Callout tone="info">
        Ganancia bruta = lo vendido menos lo que costó esa mercadería. No incluye sueldos, alquiler ni impuestos: es lo que te deja la carne antes de
        los gastos.
      </Callout>
      <Card>
        <CardHeader title="Ganancia por período" />
        <CardBody>
          <TrendChart
            points={d.series.map((s) => ({ date: s.date, salesCents: Math.max(0, s.profitCents), salesCount: 0 }))}
            granularity={d.period.granularity}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Por categoría" actions={<ExcelButton onClick={excel} />} />
        <CardBody flush>
          <table className="bf-table">
            <thead>
              <tr>
                <th>Categoría</th>
                <th style={{ textAlign: 'right' }}>Ventas</th>
                <th style={{ textAlign: 'right' }}>Costo</th>
                <th style={{ textAlign: 'right' }}>Ganancia</th>
                <th style={{ textAlign: 'right' }}>Margen</th>
              </tr>
            </thead>
            <tbody>
              {d.byCategory.map((c) => (
                <tr key={c.categoryName}>
                  <td className="bf-cell-main">{c.categoryName}</td>
                  <td className="bf-num" style={{ textAlign: 'right' }}>
                    {formatMoney(c.salesCents)}
                  </td>
                  <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                    {formatMoney(c.costCents)}
                  </td>
                  <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatMoney(c.profitCents)}
                  </td>
                  <td className={`bf-num ${(c.marginRatio ?? 1) < 0.15 ? 'bf-text-danger' : 'bf-cell-sub'}`} style={{ textAlign: 'right' }}>
                    {pct(c.marginRatio)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- costo vs precio
function PricesTab({ period }: { period: Period }) {
  const q = usePeriodQuery<PriceStatsDTO>('prices', '/stats/prices', period);
  const [onlyLost, setOnlyLost] = useState(true);
  const d = q.data;
  if (!d) return <Skeleton style={{ height: 300 }} />;
  const lost = (r: PriceStatsDTO['rows'][number]) => r.marginStart !== null && r.marginNow !== null && r.marginNow < r.marginStart - 0.005;
  const rows = d.rows.filter((r) => !onlyLost || lost(r));
  const excel = () =>
    downloadCsv(
      fileName('costo-vs-precio', d.period),
      [
        'Producto',
        'Costo inicio ($)',
        'Costo hoy ($)',
        'Aumento costo %',
        'Precio inicio ($)',
        'Precio hoy ($)',
        'Aumento precio %',
        'Margen inicio %',
        'Margen hoy %',
        'Precio sugerido ($)',
      ],
      d.rows.map((r) => [
        r.name,
        pesos(r.costStartCents),
        pesos(r.costNowCents),
        pctNum(r.costChange),
        pesos(r.priceStartCents),
        pesos(r.priceNowCents),
        pctNum(r.priceChange),
        pctNum(r.marginStart),
        pctNum(r.marginNow),
        pesos(r.suggestedPriceCents),
      ]),
    );
  return (
    <>
      <Callout tone="info">
        Compara lo que te costaba cada producto (por kilo) al empezar el período con lo que te cuesta hoy, y lo mismo con el precio de venta. Si el
        costo subió más que el precio, perdiste margen: el <strong>precio sugerido</strong> es el que te devuelve el margen que tenías.
      </Callout>
      <Card>
        <CardHeader
          title={`${d.rows.filter(lost).length} productos perdieron margen`}
          subtitle={periodText(d.period)}
          actions={
            <>
              <label className="bf-check">
                <input type="checkbox" checked={onlyLost} onChange={(e) => setOnlyLost(e.target.checked)} /> Solo los que perdieron margen
              </label>
              <ExcelButton onClick={excel} />
            </>
          }
        />
        <CardBody flush>
          {rows.length === 0 ? (
            <EmptyState icon={<TrendingUp />} title={onlyLost ? 'Ningún producto perdió margen en este período' : 'Sin datos de costo'} />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Costo</th>
                    <th style={{ textAlign: 'right' }}>Precio</th>
                    <th style={{ textAlign: 'right' }}>Margen</th>
                    <th style={{ textAlign: 'right' }}>Precio sugerido</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.productId}>
                      <td className="bf-cell-main">{r.name}</td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {r.costNowCents !== null ? formatMoney(r.costNowCents) : '—'}
                        <div className={r.costChange && r.costChange > 0 ? 'bf-text-danger bf-cell-sub' : 'bf-cell-sub'}>
                          {r.costStartCents !== null ? `antes ${formatMoney(r.costStartCents)} · ${signed(r.costChange)}` : ''}
                        </div>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {r.priceNowCents !== null ? formatMoney(r.priceNowCents) : '—'}
                        <div className="bf-cell-sub">
                          {r.priceStartCents !== null ? `antes ${formatMoney(r.priceStartCents)} · ${signed(r.priceChange)}` : ''}
                        </div>
                      </td>
                      <td className={`bf-num ${lost(r) ? 'bf-text-danger' : ''}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                        {pct(r.marginNow)}
                        <div className="bf-cell-sub">antes {pct(r.marginStart)}</div>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {lost(r) && r.suggestedPriceCents ? formatMoney(r.suggestedPriceCents) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- sucursales
function BranchesTab({ period }: { period: Period }) {
  const q = usePeriodQuery<BranchStatsDTO>('branches', '/stats/branches', period);
  const d = q.data;
  if (!d) return <Skeleton style={{ height: 300 }} />;
  const max = Math.max(...d.rows.map((r) => r.salesCents), 1);
  const showCost = d.rows.some((r) => r.costCents !== null);
  const excel = () =>
    downloadCsv(
      fileName('sucursales', d.period),
      [
        'Sucursal',
        'Ventas ($)',
        'Tickets',
        'Ticket promedio ($)',
        'Kilos',
        ...(showCost ? ['Ganancia ($)', 'Margen %', 'Merma ($)', 'Merma %'] : []),
      ],
      d.rows.map((r) => [
        r.name,
        pesos(r.salesCents),
        r.count,
        pesos(r.avgTicketCents),
        kilos(r.grams),
        ...(showCost ? [pesos(r.profitCents), pctNum(r.marginRatio), pesos(r.wasteCostCents), pctNum(r.wasteRatio)] : []),
      ]),
    );
  return (
    <Card>
      <CardHeader title="Comparativo de sucursales" subtitle={periodText(d.period)} actions={<ExcelButton onClick={excel} />} />
      <CardBody flush>
        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Sucursal</th>
                <th style={{ textAlign: 'right' }}>Ventas</th>
                <th style={{ textAlign: 'right' }}>Tickets</th>
                <th style={{ textAlign: 'right' }}>Ticket promedio</th>
                <th style={{ textAlign: 'right' }}>Kilos</th>
                {showCost && <th style={{ textAlign: 'right' }}>Margen</th>}
                {showCost && <th style={{ textAlign: 'right' }}>Merma</th>}
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.branchId}>
                  <td>
                    <span className="bf-cell-main">{r.name}</span>
                    <div className="bf-stat-bar">
                      <span style={{ width: `${(r.salesCents / max) * 100}%` }} />
                    </div>
                  </td>
                  <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatMoney(r.salesCents)}
                  </td>
                  <td className="bf-num" style={{ textAlign: 'right' }}>
                    {r.count}
                  </td>
                  <td className="bf-num" style={{ textAlign: 'right' }}>
                    {formatMoney(r.avgTicketCents)}
                  </td>
                  <td className="bf-num" style={{ textAlign: 'right' }}>
                    {formatKilos(r.grams)}
                  </td>
                  {showCost && (
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {pct(r.marginRatio)}
                      <div className="bf-cell-sub">{formatMoney(r.profitCents ?? 0)}</div>
                    </td>
                  )}
                  {showCost && (
                    <td className={`bf-num ${(r.wasteRatio ?? 0) > 0.03 ? 'bf-text-danger' : ''}`} style={{ textAlign: 'right' }}>
                      {pct(r.wasteRatio)}
                      <div className="bf-cell-sub">{formatMoney(r.wasteCostCents ?? 0)}</div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
